import { Button, Menu, Portal } from '@chakra-ui/react';
import React, { FC } from 'react';
import { Group, Item, MenuProps } from './types';
import { MenuItem } from './menu-item';
import { OptionGroupBlock } from './option-group';

export const DropdownMenu: FC<MenuProps> = ({
  colorScheme,
  disabled,
  menuButton,
  menuButtonText,
  menuButtonIcon,
  menuButtonRightIcon,
  groups = [],
  items = [],
  placement = 'bottom-start',
  preventOverflow,
  isMenuItemOptions = false,
  variant,
  w,
  menuListWidth,
  menuListMaxHeight,
  usePortal,
  isLazy,
  menuListClassName,
  isOpen,
  onOpen,
  onClose,
  'aria-label': ariaLabel,
}) => {
  const menuItems = (items: Item[]) => {
    return items.map((item, index) => {
      const key = `${typeof item.text === 'string' ? item.text : 'item'}-${item.value}-${index}`;
      return (
        <MenuItem
          key={key}
          item={item}
          isMenuItemOptions={isMenuItemOptions && item.value !== undefined}
          value={item.value}
        ></MenuItem>
      );
    });
  };

  const menuGroups = (groups: Group[]) => {
    return groups.map((group, index) => {
      const key = `${group.groupTitle}-${index}`;
      const containsSubmenu = group.items.some(item => !!item.items?.length);
      if (isMenuItemOptions && !containsSubmenu) {
        return (
          <div key={key}>
            <OptionGroupBlock group={group} />
            {!!group.hasDivider && <Menu.Separator />}
          </div>
        );
      }
      return (
        <div key={key}>
          <Menu.ItemGroup>
            {!!group.groupTitle && <Menu.ItemGroupLabel>{group.groupTitle}</Menu.ItemGroupLabel>}
            {menuItems(group.items)}
          </Menu.ItemGroup>
          {!!group.hasDivider && <Menu.Separator />}
        </div>
      );
    });
  };
  /**
   * DOM
   */
  const MenuContent = (
    <Menu.Positioner>
      <Menu.Content
        className={menuListClassName}
        w={menuListWidth}
        maxH={menuListMaxHeight}
        overflowY={menuListMaxHeight ? 'auto' : undefined}
        data-object-type="core-dropdown-menu-list"
      >
        {!!groups && menuGroups(groups)}
        {!!items && menuItems(items)}
      </Menu.Content>
    </Menu.Positioner>
  );
  return (
    <Menu.Root
      data-object-type="core-dropdown-menu"
      // `preventOverflow` maps to the positioner's `slide` option.
      positioning={{ placement, slide: preventOverflow }}
      closeOnSelect={!isMenuItemOptions}
      lazyMount={isLazy}
      open={isOpen}
      onOpenChange={details => (details.open ? onOpen?.() : onClose?.())}
    >
      <Menu.Trigger asChild>
        {menuButton || (
          <Button
            data-object-type="core-dropdown-menu-button"
            /* design-system recipe defines the named button variants; the strict
               Chakra type only knows the built-in variants (no typegen yet). */
            variant={variant as never}
            w={w}
            disabled={disabled}
            colorPalette={colorScheme}
            aria-label={ariaLabel}
          >
            {!!menuButtonText && <>{menuButtonText}</>}
            {!!menuButtonIcon && <>{menuButtonIcon}</>}
            {!!menuButtonRightIcon && <>{menuButtonRightIcon}</>}
          </Button>
        )}
      </Menu.Trigger>
      {usePortal ? <Portal>{MenuContent}</Portal> : MenuContent}
    </Menu.Root>
  );
};
